import { FC, useState, useMemo } from 'react'
import styled from 'styled-components'

import { FullScreenModal } from '@app/ui/Modal'
import { Input, Accordion } from '@app/ui'
import { Icon, IconColor, IconSize, IconTypes } from '@app/ui/Icon'
import TabSelector from '../TabSelector/TabSelector'
import { ContactSupportCard } from './ContactSupportCard'
import { ContactUsModal } from '../ContactUsModal'
import {
  FAQ_CATEGORIES,
  TUTORIAL_CATEGORIES,
  TUTORIALS_DATA,
  FAQ_DATA,
} from './constants'
import { useTranslation } from 'react-i18next'

interface FaqTutorialsModalProps {
  open: boolean
  onClose: () => void
}

const FaqContent: FC<{ searchQuery: string }> = ({ searchQuery }) => {
  const { t } = useTranslation()
  const [ selectedCategory, setSelectedCategory ] = useState('general')

  const filteredFaqs = useMemo(() => {
    let faqs = FAQ_DATA

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      faqs = faqs.filter(
        faq =>
          faq.question.toLowerCase().includes(query) ||
          faq.answer.toLowerCase().includes(query)
      )
    } else {
      faqs = faqs.filter(faq => faq.category === selectedCategory)
    }

    return faqs
  }, [ searchQuery, selectedCategory ])

  const categoryTabs = FAQ_CATEGORIES.map(cat => ({
    id: cat.id,
    label: t(cat.label),
  }))

  return (
    <FaqContentWrapper>
      <TabSelector
        tabs={categoryTabs}
        defaultTabId="general"
        variant="pill"
        hideContent
        onChange={setSelectedCategory}
      />
      <FaqList>
        {filteredFaqs.map(faq => (
          <StyledAccordion
            key={faq.id}
            title={faq.question}
            variant="plusMinus"
            iconSize={12}
            iconColor={IconColor.viloet400}
          >
            <AccordionAnswer>{faq.answer}</AccordionAnswer>
          </StyledAccordion>
        ))}
        {filteredFaqs.length === 0 && (
          <NoResults>{t('supportBottomSheet.noResultsFoundForSearchquery', { searchQuery })}</NoResults>
        )}
      </FaqList>
    </FaqContentWrapper>
  )
}

const TutorialsContent: FC<{ searchQuery: string }> = ({ searchQuery }) => {
  const { t } = useTranslation()
  const [ selectedCategory, setSelectedCategory ] = useState('general')

  const filteredTutorials = useMemo(() => {
    let tutorials = TUTORIALS_DATA

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      tutorials = tutorials.filter(tutorial =>
        tutorial.title.toLowerCase().includes(query)
      )
    } else {
      tutorials = tutorials.filter(tutorial => tutorial.category === selectedCategory)
    }

    return tutorials
  }, [ searchQuery, selectedCategory ])

  const categoryTabs = TUTORIAL_CATEGORIES.map(cat => ({
    id: cat.id,
    label: t(cat.label),
  }))

  return (
    <TutorialsContentWrapper>
      <TabSelector
        tabs={categoryTabs}
        defaultTabId="general"
        variant="pill"
        hideContent
        onChange={setSelectedCategory}
      />
      <TutorialsList>
        {filteredTutorials.map(tutorial => (
          <TutorialCard key={tutorial.id}>
            <TutorialHeader>
              <TutorialIcon>
                <Icon type={IconTypes.brand} color={IconColor.white} width={16} height={16} />
              </TutorialIcon>
              <TutorialTitleText>{tutorial.title}</TutorialTitleText>
            </TutorialHeader>
            <VideoWrapper>
              <YouTubeIframe
                src={`https://www.youtube.com/embed/${tutorial.videoId}`}
                title={tutorial.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </VideoWrapper>
          </TutorialCard>
        ))}
        {filteredTutorials.length === 0 && (
          <NoResults>{t('supportBottomSheet.noTutorialsFoundForSearchquery', { searchQuery })}</NoResults>
        )}
      </TutorialsList>
    </TutorialsContentWrapper>
  )
}

export const FaqTutorialsModal: FC<FaqTutorialsModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation()
  const [ searchQuery, setSearchQuery ] = useState('')
  const [ activeTab, setActiveTab ] = useState('faq')
  const [ openContactUs, setOpenContactUs ] = useState(false)

  const tabs = [
    {
      id: 'faq',
      label: 'FAQ',
    },
    {
      id: 'tutorials',
      label: t('supportBottomSheet.tutorials'),
    },
  ]

  return (
    <FullScreenModal
      open={open}
      onClose={onClose}
      header={t('supportBottomSheet.faqTutorials')}
      zIndex={10001}
      showBackButton
      hideLogo
      titleAlign="left"
    >
      <ModalContent>
        <TabSelector
          tabs={tabs}
          defaultTabId="faq"
          onChange={setActiveTab}
          marginBottom={16}
          hideContent
        />

        <SearchWrapper>
          <Input
            placeholder={t('supportBottomSheet.whatAreYouLookingFor')}
            value={searchQuery}
            onChange={setSearchQuery}
            leftIcon={
              <Icon
                type={IconTypes.searchIcon}
                color={IconColor.zinc500}
                size={IconSize.small}
              />
            }
            backgroundColor="var(--bg-tertiary)"
            borderColor="transparent"
          />
        </SearchWrapper>

        {activeTab === 'faq' && <FaqContent searchQuery={searchQuery} />}

        {activeTab === 'tutorials' && <TutorialsContent searchQuery={searchQuery} />}

        <ContactSupportWrapper>
          <ContactSupportCard onContactClick={() => setOpenContactUs(true)} />
        </ContactSupportWrapper>
      </ModalContent>

      <ContactUsModal
        open={openContactUs}
        onClose={() => setOpenContactUs(false)}
      />
    </FullScreenModal>
  )
}

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 32px 20px;
  height: 100%;
`

const SearchWrapper = styled.div`
  margin-bottom: 16px;
`

const FaqContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledAccordion = styled(Accordion)`
  border-bottom: 1px solid var(--zinc-800, #27272A);
`

const AccordionAnswer = styled.p`
  color: var(--text-quaternary, #A1A1AA);
  padding: 0 12px;
  margin: 0;
`

const NoResults = styled.p`
  text-align: center;
  color: var(--text-quaternary, #A1A1AA);
  padding: 32px 16px;
`

const TutorialsContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  overflow-y: auto;
  scrollbar-color: var(--scrollbar-color) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--scrollbar-color);
    border-radius: 3px;
  }
`

const TutorialsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const TutorialCard = styled.div`
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
`

const TutorialHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`

const TutorialIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--violet-600) 0%, var(--violet-900) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const TutorialTitleText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: var(--text-primary, #FAFAFA);
`

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  border-radius: 12px;
  overflow: hidden;
`

const YouTubeIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`

const ContactSupportWrapper = styled.div`
  margin-top: auto;
  padding-top: 24px;
`

export default FaqTutorialsModal
